/**
 * Smith Watchlist Hub — Iter321.
 *
 * "Empresas que Acompanho" — the dedicated feed for followed companies.
 * Two-pane layout:
 *   • Left: list of every followed company (grouped by priority) with an
 *     unseen-signal badge; click to focus.
 *   • Right: chronological feed of new signals for the selected watch (or
 *     "all" when nothing is selected), with per-signal ack + rescan-now.
 *
 * Backend contract:
 *   GET  /api/smith/watchlist          → list + unseen counts
 *   GET  /api/smith/watchlist/feed     → global feed of signals
 *   POST /api/smith/watchlist/{id}/mark-read
 *   POST /api/smith/watchlist/{id}/rescan-now
 *   DELETE /api/smith/watchlist/{id}
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, BookmarkCheck, CheckCheck, Zap, Trash2,
  ExternalLink, Search, Filter, Star,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import HelpButton from "../../components/tutorials/HelpButton";
import { useClaireContext } from "../../hooks/useClaireContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";

const PRIORITY_COLOR = {
  high:   { bg: "bg-red-500/15",     border: "border-red-400/30",     text: "text-red-200" },
  normal: { bg: "bg-slate-500/10",   border: "border-white/10",       text: "text-slate-300" },
  low:    { bg: "bg-slate-500/5",    border: "border-white/5",        text: "text-slate-500" },
};

const TYPE_ICON = {
  security_hiring:   "🛡️",  expansion:        "🏗️",  crime:           "🚨",
  management_change: "🎯",  new_facility:     "🏢",  obra_started:    "🏗️",
  obra_awarded:      "📜",  contract_won:     "💰",  acquisition:     "🤝",
  funding:           "💵",  product_launch:   "🚀",  explicit_need:   "📢",
  job_posting:       "📌",  sunbiz_new:       "🆕",  foreclosure:     "⚖️",
};

const friendlyError = (e, fb = "Failed") =>
  e?.response?.data?.detail || e?.response?.data?.error?.message || e?.message || fb;

export default function SmithWatchlist() {
  const [watches, setWatches] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // watch.id | null
  const [unseenOnly, setUnseenOnly] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [w, f] = await Promise.all([
        axios.get(`${API}/smith/watchlist`),
        axios.get(`${API}/smith/watchlist/feed?limit=100${unseenOnly ? "&unseen_only=true" : ""}`),
      ]);
      setWatches(w.data.items || []);
      setFeed(f.data.items || []);
    } catch (e) { toast.error(friendlyError(e, "Failed to load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [unseenOnly]);

  const filteredWatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    let items = watches;
    if (q) items = items.filter((w) =>
      (w.company_name || "").toLowerCase().includes(q) ||
      (w.city || "").toLowerCase().includes(q) ||
      (w.segment || "").toLowerCase().includes(q));
    return items;
  }, [watches, search]);

  const visibleFeed = useMemo(() =>
    selected ? feed.filter((f) => f.watch_id === selected) : feed,
    [feed, selected]);

  const totalUnseen = watches.reduce((n, w) => n + (w.unseen_signals || 0), 0);

  // Iter326 · Publish page context so Claire quotes the right filters.
  useClaireContext({
    page: "watchlist",
    filters: { unseen_only: unseenOnly, search: search || undefined },
    visible_count: filteredWatches.length,
    total_count: watches.length,
    project: "Smith Protective",
  });

  const markRead = async (watchId) => {
    setBusyId(watchId);
    try {
      const { data } = await axios.post(`${API}/smith/watchlist/${watchId}/mark-read`);
      toast.success(`Acked ${data.acked} signals`);
      load();
    } catch (e) { toast.error(friendlyError(e)); }
    finally { setBusyId(null); }
  };

  const rescanNow = async (watchId) => {
    setBusyId(watchId);
    try {
      const { data } = await axios.post(`${API}/smith/watchlist/${watchId}/rescan-now`,
        {}, { timeout: 60000 });
      toast.success(`Rescan complete · ${data.signals_written} signals written`);
      load();
    } catch (e) { toast.error(friendlyError(e, "Rescan failed")); }
    finally { setBusyId(null); }
  };

  const unfollow = async (watchId, name) => {
    if (!confirm(`Unfollow "${name}"?`)) return;
    try {
      await axios.delete(`${API}/smith/watchlist/${watchId}`);
      toast.success("Unfollowed");
      if (selected === watchId) setSelected(null);
      load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: NAVY }}
         data-testid="smith-watchlist-page">
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-watchlist-header"
          title="Empresas que Acompanho"
          subtitle={`${watches.length} companies · ${totalUnseen} unseen signals`}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#10B981 0%,#065F46 100%)", color: "white" }}>
              <BookmarkCheck className="w-5 h-5" />
            </div>
          }
        >
          <HelpButton tutorialId="watchlist" />
          <button type="button" onClick={() => setUnseenOnly((v) => !v)}
            data-testid="watchlist-toggle-unseen"
            className={`h-9 px-3 rounded-lg text-[12.5px] font-bold inline-flex items-center gap-1.5 ${unseenOnly ? "bg-[#10B981]/20 text-emerald-300 border border-emerald-400/40" : "text-slate-300 border border-white/15 hover:border-white/30"}`}>
            <Filter className="w-3.5 h-3.5" />
            {unseenOnly ? "Unseen only" : "All signals"}
          </button>
          <button type="button" onClick={load} disabled={loading}
            data-testid="watchlist-refresh"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-white/30 inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </AppHeader>

        <main className="px-3 sm:px-6 lg:px-8 py-4 max-w-[1500px] mx-auto pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-[380px,1fr] gap-4">
            {/* ── Left: watched companies ── */}
            <section data-testid="watchlist-list"
                     className="rounded-2xl border border-white/10 overflow-hidden"
                     style={{ background: "rgba(15,23,42,0.7)" }}>
              <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
                <Search className="w-3.5 h-3.5 text-slate-500" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Search followed companies"
                       data-testid="watchlist-search"
                       className="w-full h-8 bg-transparent text-[12.5px] outline-none placeholder:text-slate-500" />
              </div>
              <div className="max-h-[70vh] overflow-y-auto divide-y divide-white/5">
                {loading && (
                  <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
                )}
                {!loading && filteredWatches.length === 0 && (
                  <div className="p-6 text-center text-[12px] text-slate-500">
                    No followed companies yet. Click <b>Follow</b> on any prospect in the Prospects/Vehicle Security pages.
                  </div>
                )}
                {filteredWatches.map((w) => {
                  const style = PRIORITY_COLOR[w.priority] || PRIORITY_COLOR.normal;
                  const active = selected === w.id;
                  return (
                    <div key={w.id}
                         data-testid={`watchlist-row-${w.id}`}
                         onClick={() => setSelected(w.id)}
                         className={`p-3 cursor-pointer hover:bg-white/5 ${active ? "bg-white/5 border-l-2 border-[#10B981]" : ""}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[9.5px] px-1.5 py-0.5 rounded font-bold uppercase tracking-widest ${style.bg} ${style.text} ${style.border} border`}>
                          {w.priority === "high" && <Star className="w-2.5 h-2.5 inline-block fill-current mr-0.5" />}
                          {w.priority}
                        </span>
                        {w.unseen_signals > 0 && (
                          <span data-testid={`watchlist-badge-${w.id}`}
                                className="text-[9.5px] font-black px-1.5 py-0.5 rounded bg-[#10B981]/25 text-emerald-200 border border-emerald-400/40 animate-pulse">
                            {w.unseen_signals} new
                          </span>
                        )}
                        <span className="ml-auto text-[9.5px] text-slate-500">
                          {w.alert_frequency}
                        </span>
                      </div>
                      <div className="text-[12.5px] font-extrabold text-white truncate" title={w.company_name}>
                        {w.company_name}
                      </div>
                      <div className="text-[10.5px] text-slate-400 truncate">
                        {[w.city, w.state, w.segment].filter(Boolean).join(" · ") || "—"}
                      </div>
                      <div className="mt-2 flex items-center gap-1">
                        <button type="button"
                                onClick={(e) => { e.stopPropagation(); markRead(w.id); }}
                                disabled={busyId === w.id || w.unseen_signals === 0}
                                data-testid={`watchlist-markread-${w.id}`}
                                title="Mark all signals as seen"
                                className="h-6 px-2 rounded text-[10px] font-bold text-slate-300 border border-white/15 hover:border-emerald-400 inline-flex items-center gap-1 disabled:opacity-30">
                          <CheckCheck className="w-2.5 h-2.5" /> Ack
                        </button>
                        <button type="button"
                                onClick={(e) => { e.stopPropagation(); rescanNow(w.id); }}
                                disabled={busyId === w.id}
                                data-testid={`watchlist-rescan-${w.id}`}
                                title="Trigger a manual re-scan now"
                                className="h-6 px-2 rounded text-[10px] font-bold text-slate-300 border border-white/15 hover:border-amber-400 inline-flex items-center gap-1 disabled:opacity-30">
                          {busyId === w.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Zap className="w-2.5 h-2.5" />}
                          Rescan
                        </button>
                        <button type="button"
                                onClick={(e) => { e.stopPropagation(); unfollow(w.id, w.company_name); }}
                                data-testid={`watchlist-unfollow-${w.id}`}
                                title="Unfollow this company"
                                className="ml-auto h-6 w-6 rounded flex items-center justify-center text-slate-500 hover:text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* ── Right: signal feed ── */}
            <section data-testid="watchlist-feed"
                     className="rounded-2xl border border-white/10 overflow-hidden"
                     style={{ background: "rgba(15,23,42,0.7)" }}>
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <span className="text-[12.5px] font-extrabold text-white">
                  {selected ? "Signals for selected company" : "Global signal feed"}
                </span>
                {selected && (
                  <button type="button" onClick={() => setSelected(null)}
                          data-testid="watchlist-feed-clear-selection"
                          className="ml-auto text-[10.5px] font-bold text-slate-400 hover:text-white">
                    Show all
                  </button>
                )}
                {!selected && (
                  <span className="ml-auto text-[10.5px] text-slate-500">
                    {visibleFeed.length} entries
                  </span>
                )}
              </div>
              <div className="max-h-[70vh] overflow-y-auto divide-y divide-white/5">
                {loading && (
                  <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
                )}
                {!loading && visibleFeed.length === 0 && (
                  <div className="p-8 text-center text-[12px] text-slate-500">
                    No signals yet. The weekly cron scans every Monday at 08:00 UTC — or hit <b>Rescan</b> on any watched company to trigger a manual sweep.
                  </div>
                )}
                {visibleFeed.map((entry, i) => {
                  const s = entry.signal || {};
                  const icon = TYPE_ICON[s.type] || "📣";
                  const evidence = s.evidence_url || s.source_url || s.url;
                  return (
                    <div key={`${entry.watch_id}-${s.id || i}`}
                         data-testid={`watchlist-signal-${i}`}
                         className="p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[16px]" aria-hidden="true">{icon}</span>
                        <span className="text-[11px] font-black uppercase tracking-widest text-emerald-300">
                          {(s.type || "signal").replace(/_/g, " ")}
                        </span>
                        <span className="text-[10.5px] text-slate-400">· {entry.company_name}</span>
                        <span className="ml-auto text-[10px] text-slate-500 tabular-nums">
                          {s.detected_at ? new Date(s.detected_at).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—"}
                        </span>
                      </div>
                      <p className="text-[12.5px] text-white leading-relaxed">
                        {s.summary || s.headline || s.title || "(no summary)"}
                      </p>
                      {s.rationale && (
                        <p className="mt-1 text-[10.5px] text-slate-400">
                          <b className="text-emerald-300">Why it matters:</b> {s.rationale}
                        </p>
                      )}
                      {evidence && (
                        <a href={evidence} target="_blank" rel="noreferrer"
                           data-testid={`watchlist-signal-evidence-${i}`}
                           className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] font-bold text-sky-300 hover:text-sky-200 underline decoration-dotted">
                          <ExternalLink className="w-3 h-3" /> Source
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
